import { useEffect, useState } from 'react';
import {
  AgxColumn,
  AgxFileUpload,
  DocumentTypes,
  Images,
  CommunityTitlesSchemeDetails,
  AgxRadio,
  AgxLabel,
  AgxBodyText,
  AgxTextInput,
  AgxControlledCheckbox,
} from '@urbanx/agx-ui-components';

interface Props {
  id: string;
  label?: string;
  multiple?: boolean;
  extensions?: string[];
  documentType?: DocumentTypes;
  isSortable?: boolean;
  onChange: (change: { id: string; value: CommunityTitlesSchemeDetails }) => void;
  defaultValue?: CommunityTitlesSchemeDetails;
  getAuthToken: () => Promise<string | void>;
  campaignId: string;
  uploadFile?: (...args: any) => Promise<any>;
  deleteFile?: (authToken: string, filePath: string) => Promise<number | undefined>;
  getFileLink?: (authToken: string, filePath: string) => Promise<any>;
  required: boolean;
  validate: boolean;
}

const CommunityTitlesScheme = ({
  id,
  label,
  defaultValue,
  getAuthToken,
  campaignId,
  onChange,
  uploadFile,
  deleteFile,
  getFileLink,
  required,
  validate,
  multiple = false,
  extensions = ['pdf'],
  documentType = DocumentTypes.CommunityManagementStatement,
  isSortable = false,
}: Props) => {
  const [communityTitlesSchemeDetails, setCommunityTitlesSchemeDetails] = useState<
    CommunityTitlesSchemeDetails | undefined
  >(defaultValue);

  const onAttachedToSellerDisclosureChanged = (value: boolean) => {
    setCommunityTitlesSchemeDetails((prev) => ({
      ...prev,
      isCommunityTitlesScheme: true,
      statementsAttachedToSellerDisclosure: value,
      communityManagementStatements: value ? undefined : prev?.communityManagementStatements,
    }));
  };

  useEffect(() => {
    if (!communityTitlesSchemeDetails) return;

    onChange({
      id,
      value: communityTitlesSchemeDetails,
    });
  }, [communityTitlesSchemeDetails]);

  return (
    <div className="agxRadio">
      {label && <AgxLabel medium>{label}</AgxLabel>}
      <div className="agxRadio-group">
        <AgxRadio
          key={`${id}-noCommunityTitlesScheme`}
          id={`${id}-noCommunityTitlesScheme`}
          label="No"
          onCheckChanged={() =>
            setCommunityTitlesSchemeDetails((prev) => ({
              ...prev,
              isCommunityTitlesScheme: false,
            }))
          }
          checked={communityTitlesSchemeDetails?.isCommunityTitlesScheme === false}
        />
        <AgxRadio
          key={`${id}-yesCommunityTitlesScheme`}
          id={`${id}-yesCommunityTitlesScheme`}
          label="Yes"
          onCheckChanged={() =>
            setCommunityTitlesSchemeDetails((prev) => ({
              ...prev,
              isCommunityTitlesScheme: true,
            }))
          }
          checked={communityTitlesSchemeDetails?.isCommunityTitlesScheme === true}
          subContent={
            communityTitlesSchemeDetails?.isCommunityTitlesScheme === true
              ? [
                  <AgxColumn key={'yesCommunityTitlesSchemeDetails'} fill extraLargeGap>
                    <AgxTextInput
                      id={`SchemeName-${id}`}
                      key={`SchemeName-${id}-${communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure}`}
                      label="Scheme Name"
                      stretch
                      defaultValue={communityTitlesSchemeDetails?.schemeName || ''}
                      onInputValueChange={({ value }) =>
                        setCommunityTitlesSchemeDetails((prev) => ({
                          ...prev,
                          isCommunityTitlesScheme: true,
                          schemeName: value,
                        }))
                      }
                      required={
                        !communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure &&
                        required
                      }
                      validate={validate}
                    />
                    <AgxTextInput
                      id={`CommunityTitleSchemeNo-${id}`}
                      key={`CommunityTitleSchemeNo-${id}-${communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure}`}
                      label="Community Title Scheme No."
                      stretch
                      defaultValue={communityTitlesSchemeDetails?.schemeNumber || ''}
                      onInputValueChange={({ value }) =>
                        setCommunityTitlesSchemeDetails((prev) => ({
                          ...prev,
                          isCommunityTitlesScheme: true,
                          schemeNumber: value,
                        }))
                      }
                      required={
                        !communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure &&
                        required
                      }
                      validate={validate}
                    />
                    <AgxFileUpload
                      id={`communityManagementStatementDocument-${id}`}
                      key={`communityManagementStatementDocument-${id}-${communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure}`}
                      dataTestId={`community-management-statement-document-${id}`}
                      title="Community Management Statement"
                      documentType={documentType}
                      fill
                      extensions={extensions}
                      multiple={multiple}
                      isSortable={isSortable}
                      fileData={communityTitlesSchemeDetails?.communityManagementStatements}
                      onValueChanged={({ value }) => {
                        setCommunityTitlesSchemeDetails((prev) => ({
                          ...prev,
                          isCommunityTitlesScheme: true,
                          communityManagementStatements: value,
                        }));
                      }}
                      getAuthToken={getAuthToken}
                      campaignId={campaignId}
                      uploadFile={uploadFile}
                      deleteFile={deleteFile}
                      getFileLink={getFileLink}
                      required={
                        !communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure &&
                        required
                      }
                      validate={validate}
                      disabled={
                        communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure ?? false
                      }
                      uploadButtonIcon={<Images.CloudUpload />}
                    />
                    <AgxControlledCheckbox
                      key={`statementsAttachedToSellerDisclosure-${id}`}
                      checked={
                        communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure ?? false
                      }
                      onValueChanged={onAttachedToSellerDisclosureChanged}
                      label="Attached to Seller Disclosure"
                      naked
                      id={id}
                    />
                  </AgxColumn>,
                ]
              : undefined
          }
        />
      </div>
      {required && validate && !communityTitlesSchemeDetails && (
        <div className="errorMessage">
          <Images.AlertCircle />
          <AgxBodyText small extraClasses={'error'}>
            {'Select Community Titles Scheme'}
          </AgxBodyText>
        </div>
      )}
    </div>
  );
};

export default CommunityTitlesScheme;
